import { Pipe, PipeTransform } from '@angular/core';
import { UnitsService } from '../services/units.service';

/**
 * The units pipes are impure so that a change to the user's display units
 * re-renders values that are already on screen — a pure pipe never re-runs,
 * because the units key is service state rather than a pipe argument.
 *
 * Impurity is only expensive when the transform body is, so each pipe
 * memoizes on its arguments plus `UnitsService.revision`. Angular creates one
 * pipe instance per binding site, so this is the same per-binding memo the
 * pure-pipe machinery would apply, with the units key folded into the key.
 * Steady-state cost is a handful of identity comparisons per change-detection
 * pass instead of a js-quantities/BigNumber conversion.
 */

@Pipe({
  name: 'unitsFormat',
  pure: false,
})
export class UnitsFormatPipe implements PipeTransform {
  constructor(private unitsService: UnitsService) {}

  private revision = -1;
  private input: unknown;
  private inType: unknown;
  private decimals: unknown;
  private hideUnit: unknown;
  private shortenTo: unknown;
  private outType: unknown;
  private result: unknown;

  transform(input, inType?, decimals?, hideUnit?, shortenTo?, outType?): unknown {
    if (
      this.revision === this.unitsService.revision &&
      this.input === input &&
      this.inType === inType &&
      this.decimals === decimals &&
      this.hideUnit === hideUnit &&
      this.shortenTo === shortenTo &&
      this.outType === outType
    ) {
      return this.result;
    }

    this.revision = this.unitsService.revision;
    this.input = input;
    this.inType = inType;
    this.decimals = decimals;
    this.hideUnit = hideUnit;
    this.shortenTo = shortenTo;
    this.outType = outType;

    if (!shortenTo) {
      this.result = this.unitsService.formatUserUnits(input, inType, decimals, hideUnit, outType);
    } else {
      this.result = this.unitsService.shortenBigNumber(input, inType, shortenTo);
    }
    return this.result;
  }
}

@Pipe({
  name: 'userDisplayUnits',
  pure: false,
})
export class UserDisplayUnitsPipe implements PipeTransform {
  constructor(private unitsService: UnitsService) {}

  private revision = -1;
  private type: unknown;
  private result: unknown;

  transform(type): unknown {
    if (this.revision === this.unitsService.revision && this.type === type) {
      return this.result;
    }

    this.revision = this.unitsService.revision;
    this.type = type;
    this.result = this.unitsService.getUserDisplayUnits(type);
    return this.result;
  }
}

@Pipe({
  name: 'unitsValue',
  pure: false,
})
export class UnitsValuePipe implements PipeTransform {
  constructor(private unitsService: UnitsService) {}

  private revision = -1;
  private input: unknown;
  private inType: unknown;
  private decimals: unknown;
  private result: unknown;

  transform(input, inType, decimals): unknown {
    if (
      this.revision === this.unitsService.revision &&
      this.input === input &&
      this.inType === inType &&
      this.decimals === decimals
    ) {
      return this.result;
    }

    this.revision = this.unitsService.revision;
    this.input = input;
    this.inType = inType;
    this.decimals = decimals;

    if (!input) {
      this.result = 0;
    } else {
      this.result = this.unitsService.convertUnits(input, inType, decimals);
    }
    return this.result;
  }
}

@Pipe({
  name: 'taskLenghValue',
})
export class TaskLenghValuePipe implements PipeTransform {
  transform(input): unknown {
    if (!input) {
      return 0;
    }
    return Math.round(input);
  }
}
